import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faPlus,
  faMagnifyingGlass,
  faPencil,
  faTrash,
  faEye,
  faEyeSlash,
  faCheck,
  faTimes,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const UserManagement = () => {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    role: "",
    isActive: "",
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    fullName: "",
    role: "admin",
    phone: "",
    department: "",
    isActive: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);

      // Only include non-empty filter values
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: 50,
      });

      if (filters.search && filters.search.trim() !== "") {
        params.append("search", filters.search);
      }
      if (filters.role && filters.role.trim() !== "") {
        params.append("role", filters.role);
      }
      if (filters.isActive && filters.isActive.trim() !== "") {
        params.append("isActive", filters.isActive);
      }

      const response = await api.get(`/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      role: "",
      isActive: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const openModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);

    if (mode === "edit" && user) {
      setFormData({
        username: user.username,
        password: "",
        email: user.email,
        fullName: user.fullName,
        role: user.role,
        phone: user.phone || "",
        department: user.department || "",
        isActive: user.isActive,
      });
    } else {
      setFormData({
        username: "",
        password: "",
        email: "",
        fullName: "",
        role: "admin",
        phone: "",
        department: "",
        isActive: true,
      });
    }

    setFormErrors({});
    setShowPassword(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      username: "",
      password: "",
      email: "",
      fullName: "",
      role: "admin",
      phone: "",
      department: "",
      isActive: true,
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = "Username is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.fullName.trim()) {
      errors.fullName = "Full name is required";
    }

    if (formData.password && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const submitData = { ...formData };
      if (!submitData.password || !submitData.password.trim()) {
        delete submitData.password;
      }

      if (modalMode === "add") {
        const response = await api.post("/users", submitData);
        const inviteStatus = response?.data?.invite?.status;
        const inviteReason = response?.data?.invite?.reason;
        if (inviteStatus === "sent") {
          setSuccessMessage(
            "User created successfully and invitation email sent.",
          );
        } else if (inviteStatus === "failed") {
          setSuccessMessage(
            `User created successfully, but invitation email failed: ${inviteReason || "unknown error"}.`,
          );
        } else if (inviteStatus === "skipped") {
          setSuccessMessage(
            `User created successfully, but invitation email was skipped: ${inviteReason || "not eligible for invitation"}.`,
          );
        } else {
          setSuccessMessage("User created successfully!");
        }
      } else {
        await api.put(`/users/${selectedUser.id}`, submitData);
        setSuccessMessage("User updated successfully!");
      }

      closeModal();
      fetchUsers();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error saving user:", error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      } else {
        setFormErrors({ general: "Failed to save user. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userToDelete) => {
    if (
      !window.confirm(
        `Are you sure you want to delete user "${userToDelete.fullName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await api.delete(`/users/${userToDelete.id}`);
      setSuccessMessage("User deleted successfully!");
      fetchUsers();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting user:", error);
      setError(error.response?.data?.message || "Failed to delete user");
    }
  };

  const handleToggleStatus = async (userToToggle) => {
    try {
      await api.patch(`/users/${userToToggle.id}/toggle-status`);
      setSuccessMessage(
        `User ${userToToggle.isActive ? "deactivated" : "activated"} successfully!`,
      );
      fetchUsers();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error toggling user status:", error);
      setError(error.response?.data?.message || "Failed to update user status");
    }
  };

  const handleResendInvite = async (userToResend) => {
    if (
      !window.confirm(
        `Resend invite to "${userToResend.fullName}" (${userToResend.email})?`,
      )
    ) {
      return;
    }

    try {
      await api.post(`/users/${userToResend.id}/resend-invite`);
      setSuccessMessage(`Invitation resent to ${userToResend.email}.`);
      fetchUsers();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error resending invite:", error);
      const message =
        error.response?.data?.message || "Failed to resend invitation";
      setError(message);
    }
  };

  const getRoleColor = (role) => {
    const colorMap = {
      admin: "bg-red-100 text-red-800",
      planner: "bg-blue-100 text-blue-800",
      inspector: "bg-green-100 text-green-800",
    };
    return colorMap[role] || "bg-gray-100 text-gray-800";
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      admin: "Administrator",
      planner: "Planner",
      inspector: "Inspector",
    };
    return roleMap[role] || role;
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start gap-2">
            <p className="text-gray-600">Manage system users and their roles</p>
            <InlineFieldTooltip
              page="user_management"
              section="page_description"
              fieldKey="user_management_overview"
            />
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon
                icon={faCheck}
                className="h-5 w-5 text-green-400"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <FontAwesomeIcon
                icon={faTimes}
                className="h-5 w-5 text-red-400"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="pl-10 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Search users..."
            />
          </div>

          <select
            value={filters.role}
            onChange={(e) => handleFilterChange("role", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Roles</option>
            <option value="admin">Administrator</option>
            <option value="planner">Planner</option>
          </select>

          <select
            value={filters.isActive}
            onChange={(e) => handleFilterChange("isActive", e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FontAwesomeIcon icon={faTimes} className="h-4 w-4 mr-2" />
            Clear Filters
          </button>

          <button
            onClick={() => openModal("add")}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FontAwesomeIcon icon={faPlus} className="h-5 w-5 mr-2" />
            Add User
          </button>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Users ({pagination.totalUsers})
            </h3>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invite
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userItem) => (
                    <tr key={userItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <FontAwesomeIcon
                                icon={faUsers}
                                className="h-6 w-6 text-gray-600"
                              />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {userItem.fullName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {userItem.username} • {userItem.email}
                            </div>
                            {userItem.department && (
                              <div className="text-xs text-gray-400">
                                {userItem.department}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(userItem.role)}`}
                        >
                          {getRoleDisplay(userItem.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={userItem.isActive}
                          onClick={() => handleToggleStatus(userItem)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            userItem.isActive ? "bg-blue-600" : "bg-gray-300"
                          }`}
                          title={
                            userItem.isActive
                              ? "Deactivate user"
                              : "Activate user"
                          }
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              userItem.isActive
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userItem.lastLogin
                          ? new Date(userItem.lastLogin).toLocaleDateString()
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {userItem.canResendInvite ? (
                          <button
                            onClick={() => handleResendInvite(userItem)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200"
                            title="Previous invite expired and account setup is incomplete"
                          >
                            <FontAwesomeIcon
                              icon={faPaperPlane}
                              className="h-3 w-3 mr-1"
                            />
                            Resend Invite
                          </button>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              userItem.invite?.status === "setup-complete"
                                ? "bg-green-100 text-green-800"
                                : userItem.invite?.status === "active"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {userItem.invite?.status === "setup-complete"
                              ? "Setup Complete"
                              : userItem.invite?.status === "active"
                                ? "Invite Active"
                                : userItem.invite?.status === "none"
                                  ? "No Invite"
                                  : "Unavailable"}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openModal("edit", userItem)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Edit user"
                          >
                            <FontAwesomeIcon
                              icon={faPencil}
                              className="h-4 w-4"
                            />
                          </button>
                          <button
                            onClick={() => handleDelete(userItem)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete user"
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="h-4 w-4"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {pagination.currentPage} of{" "}
                  {pagination.totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit User Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900">
                    <span>
                      {modalMode === "add" ? "Add New User" : "Edit User"}
                    </span>
                    <InlineFieldTooltip
                      page="user_management"
                      section="modal_titles"
                      fieldKey="user_form"
                      fallbackText="Create or edit user access and profile details."
                      title="User Form"
                    />
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                  </button>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  autoComplete="off"
                >
                  {/* Hidden decoy fields reduce aggressive browser autofill in admin forms. */}
                  <input
                    type="text"
                    name="fakeUsername"
                    autoComplete="username"
                    tabIndex={-1}
                    className="hidden"
                    aria-hidden="true"
                  />
                  <input
                    type="password"
                    name="fakePassword"
                    autoComplete="current-password"
                    tabIndex={-1}
                    className="hidden"
                    aria-hidden="true"
                  />
                  {/* General Error */}
                  {formErrors.general && (
                    <div className="text-red-600 text-sm">
                      {formErrors.general}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                        formErrors.username
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                    />
                    {formErrors.username && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.username}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                        formErrors.email ? "border-red-300" : "border-gray-300"
                      }`}
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className={`block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                        formErrors.fullName
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                    />
                    {formErrors.fullName && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.fullName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password{" "}
                      {modalMode === "add"
                        ? "(optional - user can use magic link)"
                        : "(leave blank to keep current)"}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        className={`block w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                          formErrors.password
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <FontAwesomeIcon
                            icon={faEyeSlash}
                            className="h-5 w-5 text-gray-400 hover:text-gray-600"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faEye}
                            className="h-5 w-5 text-gray-400 hover:text-gray-600"
                          />
                        )}
                      </button>
                    </div>
                    {formErrors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {formErrors.password}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role *
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="admin">Administrator</option>
                      <option value="planner">Planner</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active account
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      {submitting
                        ? "Saving..."
                        : modalMode === "add"
                          ? "Create User"
                          : "Update User"}
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
